import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';

import { Button, Chip, FieldLabel, Flex, TextField } from '@servicetitan/anvil2';

import { isValidSenderDomain } from '../../utils/validators';
import {
    normalizeDomain,
    normalizeWarmupDomains,
    replaceWarmupSuffix,
} from '../../utils/warmup-domains';

interface WarmupDomainsFieldProps {
    value: string[];
    onChange: (domains: string[]) => void;
    senderDomain: string;
    disabled?: boolean;
}

export const WarmupDomainsField = observer(
    ({ value, onChange, senderDomain, disabled }: WarmupDomainsFieldProps) => {
        const [inputValue, setInputValue] = useState('');

        const sender = normalizeDomain(senderDomain);
        const hasValidSender = isValidSenderDomain(sender);

        /*
         * Chips always reflect the current sender domain: when it changes we re-point
         * every chip's suffix to it. Read the latest list via a ref so the effect only
         * runs on sender-domain changes, not on add/remove.
         */
        const valueRef = useRef(value);
        valueRef.current = value;

        useEffect(() => {
            if (!hasValidSender) {
                return;
            }

            const remapped = replaceWarmupSuffix(valueRef.current, sender);

            if (remapped.join(',') !== valueRef.current.join(',')) {
                onChange(remapped);
            }
        }, [sender, hasValidSender, onChange]);

        const handleAdd = () => {
            const prefix = normalizeDomain(inputValue);

            if (!prefix || !hasValidSender) {
                return;
            }

            onChange(normalizeWarmupDomains([...value, `${prefix}.${sender}`]));
            setInputValue('');
        };

        const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleAdd();
            }
        };

        const handleRemove = (domain: string) => {
            onChange(value.filter(existing => existing !== domain));
        };

        const isInputDisabled = disabled || !hasValidSender;

        return (
            <Flex direction="column" gap="2">
                <FieldLabel>Warm-up Domains</FieldLabel>

                {value.length > 0 && (
                    <Flex wrap="wrap" gap="1">
                        {value.map(domain => (
                            <Chip
                                key={domain}
                                label={domain}
                                onClose={disabled ? undefined : () => handleRemove(domain)}
                            />
                        ))}
                    </Flex>
                )}

                <Flex alignItems="flex-start" gap="2">
                    <TextField
                        flexGrow={1}
                        value={inputValue}
                        description={
                            hasValidSender
                                ? 'Enter the subdomain prefix and press "Enter" or the "Add" button.'
                                : 'Enter a valid Email Sender Domain first.'
                        }
                        suffix={hasValidSender ? `.${sender}` : undefined}
                        disabled={isInputDisabled}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setInputValue(event.target.value)
                        }
                        onKeyDown={handleKeyDown}
                    />
                    <Button appearance="secondary" disabled={isInputDisabled} onClick={handleAdd}>
                        Add
                    </Button>
                </Flex>
            </Flex>
        );
    },
);
